import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, LoadingSkeleton } from '@nearrme/ui-web';
import { listPermissions } from '../api';

export function AdminPermissionsPage() {
  const permissions = useQuery({ queryKey: ['admin', 'permissions'], queryFn: listPermissions });
  const grouped = useMemo(() => {
    return (permissions.data ?? []).reduce<Record<string, typeof permissions.data>>((groups, permission) => {
      const category = permission.category || 'Other';
      groups[category] = groups[category] ?? [];
      groups[category]!.push(permission);
      return groups;
    }, {});
  }, [permissions.data]);

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Permissions</h1>
      <p className="mt-1 text-slate-600 dark:text-slate-400">
        Permission catalog used to control which roles can access screens and operational data.
      </p>

      {permissions.isLoading && (
        <div className="mt-6 space-y-2">
          {[1, 2, 3, 4].map((item) => <LoadingSkeleton key={item} className="h-16 w-full" />)}
        </div>
      )}

      {permissions.error && (
        <Card padded className="mt-6">
          <div className="text-sm text-red-600 dark:text-red-400">{(permissions.error as Error).message}</div>
        </Card>
      )}

      <div className="mt-6 grid gap-5">
        {Object.entries(grouped).map(([category, rows]) => (
          <Card key={category} padded>
            <h2 className="text-lg font-bold text-slate-950 dark:text-slate-50">{category}</h2>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-left dark:border-slate-800">
                  <tr>
                    <th className="py-2 pr-4 font-semibold text-slate-700 dark:text-slate-300">Permission</th>
                    <th className="py-2 pr-4 font-semibold text-slate-700 dark:text-slate-300">Code</th>
                    <th className="py-2 pr-4 font-semibold text-slate-700 dark:text-slate-300">Screen</th>
                    <th className="py-2 font-semibold text-slate-700 dark:text-slate-300">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {(rows ?? []).map((permission) => (
                    <tr key={permission.permissionCode}>
                      <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{permission.displayName}</td>
                      <td className="py-3 pr-4 font-mono text-xs text-slate-500 dark:text-slate-400">{permission.permissionCode}</td>
                      <td className="py-3 pr-4 font-mono text-xs text-slate-500 dark:text-slate-400">{permission.screenPath}</td>
                      <td className="py-3 text-slate-600 dark:text-slate-400">{permission.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
